/**
 * PATH: apps/qa/src/components/useReportData.ts
 * PURPOSE: Data layer for QA report view - queries, mutations, handlers, computed values.
 * WHY: Extracted from ReportView.tsx to keep the component under 300 lines.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { evidenceApi, reportDraftApi } from '@/lib/api'
import type { EvidenceItem, IncludedSections } from '@/lib/api'
import api from '@/lib/api'

export function useReportData(
  inspectionId: string,
  inspectionName: string,
  floors: Record<string, unknown>[],
) {
  const { data: allEvidenceData } = useQuery({
    queryKey: ['qa-all-evidence-for-report', inspectionId],
    queryFn: async () => {
      try {
        const res = await evidenceApi.getEvidence({ inspection_id: inspectionId, page_size: 500 })
        return res.results || []
      } catch { return [] }
    },
    staleTime: 30_000,
  })
  const allEvidence: EvidenceItem[] = useMemo(() => allEvidenceData || [], [allEvidenceData])

  const evidenceByArea = useMemo(() => {
    const map: Record<string, EvidenceItem[]> = {}
    for (const floor of floors) {
      for (const area of ((floor.areas || []) as Record<string, unknown>[])) {
        map[area.id as string] = []
      }
    }
    for (const ev of allEvidence) {
      if (ev.area) {
        if (!map[ev.area]) map[ev.area] = []
        map[ev.area].push(ev)
      } else {
        if (!map['__general__']) map['__general__'] = []
        map['__general__'].push(ev)
      }
    }
    return map
  }, [allEvidence, floors])

  const { data: allDrafts, refetch: refetchDrafts } = useQuery({
    queryKey: ['qa-report-drafts-for-inspection', inspectionId],
    queryFn: async () => {
      try {
        const all = await reportDraftApi.getDrafts({ page_size: 50 })
        return (all.results || []).filter(d => d.inspection === inspectionId)
      } catch { return [] }
    },
    retry: 1, staleTime: 30_000,
  })
  const drafts = useMemo(() => allDrafts || [], [allDrafts])
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null)
  const activeDraft = drafts.find(d => d.id === activeDraftId) || drafts[0] || null

  useEffect(() => {
    if (!activeDraftId && drafts.length > 0) setActiveDraftId(drafts[0].id)
  }, [drafts, activeDraftId])

  const [title, setTitle] = useState('')
  const [includedSections, setIncludedSections] = useState<IncludedSections>({})
  const [userNotes, setUserNotes] = useState<Record<string, string>>({})
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  useEffect(() => {
    if (activeDraft) {
      setTitle(activeDraft.title)
      setIncludedSections(activeDraft.included_sections || {})
      setUserNotes(activeDraft.user_notes || {})
    } else {
      setTitle(`QA Report \u2014 ${inspectionName}`)
      setIncludedSections({})
      setUserNotes({})
    }
  }, [activeDraft?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const createDraftMutation = useMutation({
    mutationFn: () => reportDraftApi.createDraft({ inspection: inspectionId, title: `QA Report \u2014 ${inspectionName}` }),
    onSuccess: (d) => { refetchDrafts(); setActiveDraftId(d.id) },
  })
  const saveMutation = useMutation({
    mutationFn: () => {
      if (!activeDraft) return createDraftMutation.mutateAsync().then(d => reportDraftApi.saveVersion(d.id, { expected_version: d.version, title, included_sections: includedSections, user_notes: userNotes }))
      return reportDraftApi.saveVersion(activeDraft.id, { expected_version: activeDraft.version, title, included_sections: includedSections, user_notes: userNotes })
    },
    onSuccess: () => refetchDrafts(),
  })
  const autoSaveMutation = useMutation({
    mutationFn: (data: { included_sections?: IncludedSections; user_notes?: Record<string, string>; title?: string }) => {
      if (!activeDraft) return Promise.resolve(null)
      return reportDraftApi.updateDraft(activeDraft.id, { ...data, expected_version: activeDraft.version })
    },
    onSuccess: () => refetchDrafts(),
  })

  const handleToggleFloor = useCallback((floorId: string) => {
    setIncludedSections(prev => {
      const current = prev.floors || []
      const allFloorIds = floors.map(f => f.id as string)
      if (current.length === 0) {
        const next = { ...prev, floors: allFloorIds.filter(id => id !== floorId) }
        autoSaveMutation.mutate({ included_sections: next }); return next
      }
      const next = current.includes(floorId) ? { ...prev, floors: current.filter(id => id !== floorId) } : { ...prev, floors: [...current, floorId] }
      autoSaveMutation.mutate({ included_sections: next }); return next
    })
  }, [floors]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleArea = useCallback((areaId: string) => {
    setIncludedSections(prev => {
      const current = prev.areas || []
      const allAreaIds = floors.flatMap(f => ((f.areas || []) as Record<string, unknown>[]).map(a => a.id as string))
      if (current.length === 0) {
        const next = { ...prev, areas: allAreaIds.filter(id => id !== areaId) }
        autoSaveMutation.mutate({ included_sections: next }); return next
      }
      const next = current.includes(areaId) ? { ...prev, areas: current.filter(id => id !== areaId) } : { ...prev, areas: [...current, areaId] }
      autoSaveMutation.mutate({ included_sections: next }); return next
    })
  }, [floors]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNoteChange = useCallback((key: string, val: string) => {
    setUserNotes(prev => { const next = { ...prev, [key]: val }; autoSaveMutation.mutate({ user_notes: next }); return next })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportPdf = useCallback(async () => {
    setIsExporting(true); setExportError(null)
    try {
      const response = await api().get('/qa/reports/export_inspection/', {
        params: { inspection_id: inspectionId, format: 'enhanced', photos: 'true', signatures: 'true' },
        responseType: 'blob',
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url
      a.download = `${inspectionName.replace(/[^a-zA-Z0-9\s-]/g, '')}_QA_Report.pdf`
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
    } catch { setExportError('Export failed. Please try again.') } finally { setIsExporting(false) }
  }, [inspectionId, inspectionName])

  const filteredFloors = useMemo(() => floors.filter(f => {
    if (!includedSections.floors || includedSections.floors.length === 0) return true
    return includedSections.floors.includes(f.id as string)
  }).map(f => ({
    ...f,
    areas: ((f.areas || []) as Record<string, unknown>[]).filter(a => {
      if (!includedSections.areas || includedSections.areas.length === 0) return true
      return includedSections.areas.includes(a.id as string)
    })
  })), [floors, includedSections])

  const totalAreas = floors.reduce((sum, f) => sum + ((f.areas as unknown[])?.length || 0), 0)
  const lockedAreas = floors.reduce((sum, f) => sum + ((f.areas || []) as Record<string, unknown>[]).filter(a => a.status === 'locked' || a.is_locked).length, 0)
  const totalPhotos = allEvidence.filter(e => (e.type === 'photo' || e.type === 'video') && e.asset_url).length
  const totalNotes = allEvidence.filter(e => e.type === 'note').length
  const completionPct = totalAreas > 0 ? Math.round((lockedAreas / totalAreas) * 100) : 0

  return {
    allEvidence, evidenceByArea, drafts, activeDraftId, setActiveDraftId, activeDraft,
    refetchDrafts, title, setTitle, includedSections, userNotes,
    isEditingTitle, setIsEditingTitle, isExporting, exportError,
    createDraftMutation, saveMutation, autoSaveMutation,
    handleToggleFloor, handleToggleArea, handleNoteChange, handleExportPdf,
    filteredFloors, totalAreas, lockedAreas, totalPhotos, totalNotes, completionPct,
  }
}
